import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { GoogleLogin } from '@react-oauth/google';
import { useAuthStore } from '../store/authStore';
import { useConfigStore } from '../store/configStore';

interface LayoutProps {
  children: React.ReactNode;
}

export default function Layout({ children }: LayoutProps) {
  const { user, isAuthenticated, isLoading, logout, checkSession } = useAuthStore();
  const generationEnabled = useConfigStore((s) => s.generation.enabled);
  const isDev = useConfigStore((s) => s.dev);
  const fetchConfig = useConfigStore((s) => s.fetchConfig);

  useEffect(() => {
    checkSession();
    fetchConfig();
  }, [checkSession, fetchConfig]);

  const handleDevLogin = async () => {
    const { apiFetch } = await import('../lib/api');
    const res = await apiFetch('/api/auth/dev-login', { method: 'POST' });
    if (res.ok) await checkSession();
    else alert('Dev login failed');
  };

  const handleLoginSuccess = async (credentialResponse: { credential?: string }) => {
    const credential = credentialResponse.credential;
    if (!credential) return;

    const { apiFetch } = await import('../lib/api');
    const res = await apiFetch('/api/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ credential }),
    });

    if (res.ok) {
      await checkSession();
    } else {
      const data = (await res.json()) as { error?: string };
      alert(data.error || 'Login failed');
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100%' }}>
      <header
        style={{
          background: 'var(--panel)',
          borderBottom: '1px solid #334155',
          padding: '0.75rem 1rem',
        }}
      >
        <div
          className="container"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: 0,
          }}
        >
          <Link to="/" style={{ fontSize: '1.25rem', fontWeight: 700 }}>
            d3-glb-viewer
          </Link>
          <nav style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
            <Link to="/library">Library</Link>
            {isAuthenticated && <Link to="/upload">Upload</Link>}
            {isAuthenticated && generationEnabled && <Link to="/generate">Generate</Link>}
            {isLoading ? null : isAuthenticated ? (
              <>
                <span style={{ color: 'var(--muted)' }}>{user?.name || user?.email}</span>
                <button onClick={logout} style={{ background: 'transparent', color: 'var(--text)' }}>
                  Logout
                </button>
              </>
            ) : (
              <>
                {isDev && (
                  <button
                    onClick={handleDevLogin}
                    style={{ background: '#334155', color: 'var(--text)', fontSize: '0.85rem' }}
                  >
                    Dev login
                  </button>
                )}
                <GoogleLogin
                  onSuccess={handleLoginSuccess}
                  onError={() => alert('Login failed')}
                  useOneTap
                  size="small"
                />
              </>
            )}
          </nav>
        </div>
      </header>
      <main style={{ flex: 1 }}>{children}</main>
      <footer
        style={{
          background: 'var(--panel)',
          borderTop: '1px solid #334155',
          padding: '1rem',
          textAlign: 'center',
          color: 'var(--muted)',
        }}
      >
        <div className="container">Cloudflare-native GLB viewer & sharing hub</div>
      </footer>
    </div>
  );
}
